package co.maytes.standupbot.dashboard.features.auth

import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.setValue
import co.maytes.standupbot.dashboard.lib.api.ApiClient
import co.maytes.standupbot.dashboard.lib.api.ApiError
import co.maytes.standupbot.dashboard.lib.api.Result
import co.maytes.standupbot.dashboard.lib.auth.CrossTabAuth
import co.maytes.standupbot.dashboard.lib.auth.TokenStore
import co.maytes.standupbot.shared.dto.auth.CurrentUserDto
import co.maytes.standupbot.shared.dto.auth.RoleDto
import com.kttipay.mvi.api.Container
import com.kttipay.mvi.api.StoreScope
import com.kttipay.mvi.dsl.store
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.flow.first
import kotlinx.coroutines.launch

class AuthContainer(
    private val scope: CoroutineScope,
    private val api: ApiClient,
    private val tokenStore: TokenStore,
) : Container<AuthContract.State, AuthContract.Intent, AuthContract.Action> {

    override val store = scope.store<AuthContract.State, AuthContract.Intent, AuthContract.Action>(
        initialState = AuthContract.State(),
    ) {
        configure(name = "Auth")
        logging()
        reduce { intent ->
            when (intent) {
                AuthContract.Intent.Bootstrap -> runBootstrap()
                AuthContract.Intent.SignOut -> runSignOut()
                AuthContract.Intent.SignOutEverywhere -> runSignOutEverywhere()
                AuthContract.Intent.RemoteSignedOut -> {
                    tokenStore.clear()
                    updateState { copy(phase = AuthPhase.Unauthenticated, user = null) }
                    action(AuthContract.Action.SignedOut)
                }
                AuthContract.Intent.MarkOnboardingComplete -> runMarkOnboardingComplete()
            }
        }
    }

    var phase: AuthPhase by mutableStateOf(AuthPhase.Loading)
        private set

    var currentUser: CurrentUserDto? by mutableStateOf(null)
        private set

    init {
        CrossTabAuth.onSignedOut { store.intent(AuthContract.Intent.RemoteSignedOut) }
        scope.launch {
            store.state.collect { snapshot ->
                phase = snapshot.phase
                currentUser = snapshot.user
            }
        }
    }

    fun bootstrap() {
        store.intent(AuthContract.Intent.Bootstrap)
    }

    suspend fun signOut() {
        store.intent(AuthContract.Intent.SignOut)
        store.actions.first { it is AuthContract.Action.SignedOut }
    }

    suspend fun signOutEverywhere(): Result<Unit, ApiError> {
        store.intent(AuthContract.Intent.SignOutEverywhere)
        return when (val a = store.actions.first { it is AuthContract.Action.SignedOut || it is AuthContract.Action.SignOutEverywhereFailed }) {
            is AuthContract.Action.SignOutEverywhereFailed -> Result.Err(a.error)
            else -> Result.Ok(Unit)
        }
    }

    suspend fun markOnboardingComplete(): Result<Unit, ApiError> {
        store.intent(AuthContract.Intent.MarkOnboardingComplete)
        return when (val a = store.actions.first { it is AuthContract.Action.OnboardingCompleted || it is AuthContract.Action.OnboardingMarkFailed }) {
            AuthContract.Action.OnboardingCompleted -> Result.Ok(Unit)
            is AuthContract.Action.OnboardingMarkFailed -> Result.Err(a.error)
            else -> error("unreachable")
        }
    }

    fun isAuthenticated(): Boolean = phase is AuthPhase.Authenticated

    fun requiresOnboarding(): Boolean =
        currentUser?.workspace?.onboardingCompleted == false

    val isAdmin: Boolean
        get() = currentUser?.role == RoleDto.OWNER || currentUser?.role == RoleDto.ADMIN

    private suspend fun StoreScope<AuthContract.State, AuthContract.Action>.runBootstrap() {
        updateState { copy(phase = AuthPhase.Loading) }
        val refreshed = tokenStore.tryRefresh()
        if (!refreshed) {
            updateState { copy(phase = AuthPhase.Unauthenticated, user = null) }
            action(AuthContract.Action.BootstrappedUnauthenticated)
            return
        }
        when (val me = api.get<CurrentUserDto>("/api/v1/me")) {
            is Result.Ok ->
                updateState {
                    copy(
                        phase = AuthPhase.Authenticated(me.value.userId),
                        user = me.value,
                    )
                }
            is Result.Err -> {
                tokenStore.clear()
                updateState { copy(phase = AuthPhase.Unauthenticated, user = null) }
                action(AuthContract.Action.BootstrappedUnauthenticated)
            }
        }
    }

    private suspend fun StoreScope<AuthContract.State, AuthContract.Action>.runSignOut() {
        api.post<Unit>("/auth/logout")
        tokenStore.clear()
        CrossTabAuth.announceSignedOut()
        updateState { copy(phase = AuthPhase.Unauthenticated, user = null) }
        action(AuthContract.Action.SignedOut)
    }

    private suspend fun StoreScope<AuthContract.State, AuthContract.Action>.runSignOutEverywhere() {
        when (val result = api.post<Unit>("/auth/sessions/revoke-all")) {
            is Result.Ok -> {
                tokenStore.clear()
                CrossTabAuth.announceSignedOut()
                updateState { copy(phase = AuthPhase.Unauthenticated, user = null) }
                action(AuthContract.Action.SignedOut)
            }
            is Result.Err -> action(AuthContract.Action.SignOutEverywhereFailed(result.error))
        }
    }

    private suspend fun StoreScope<AuthContract.State, AuthContract.Action>.runMarkOnboardingComplete() {
        when (val res = api.post<CurrentUserDto>("/api/v1/me/onboarding/complete")) {
            is Result.Ok -> {
                updateState { copy(user = res.value) }
                action(AuthContract.Action.OnboardingCompleted)
            }
            is Result.Err ->
                action(AuthContract.Action.OnboardingMarkFailed(res.error))
        }
    }
}
